import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  Mail,
  Lock,
  ArrowRight,
  Eye,
  EyeOff,
  Sparkles,
  MapPin,
  Camera,
  FileText,
  Bell,
  MessageCircle,
  Users,
  BadgeCheck,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { BRAND } from "@/brand";

const CHIPS = [
  { icon: MapPin, label: "Field Ops" },
  { icon: Camera, label: "Site Photos" },
  { icon: FileText, label: "Quotations" },
  { icon: Bell, label: "Reminders" },
  { icon: MessageCircle, label: "WhatsApp" },
  { icon: Users, label: "Multi-role" },
  { icon: BadgeCheck, label: "GST-Ready" },
];

const FEATURES = [
  { title: "Quotations · Invoices", sub: "GST-compliant PDF" },
  { title: "GPS-tagged visits", sub: "300m follow-up fence" },
  { title: "T-1 Day Reminders", sub: "Auto-email HQ" },
];

export default function PortalLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      nav(user.role === "admin" ? "/admin" : user.role === "shop_worker" ? "/shop" : "/field", { replace: true });
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      if (!res?.ok) {
        toast.error(res?.error || "Login failed. Please check your credentials.");
        return;
      }
      toast.success("Welcome back!");
      const u = res.user;
      nav(u.role === "admin" ? "/admin" : u.role === "shop_worker" ? "/shop" : "/field", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="portal-login-page" className="min-h-screen w-full grid lg:grid-cols-2 bg-slate-50">
      {/* ==================== LEFT: DARK HERO ==================== */}
      <section className="relative overflow-hidden bg-slate-950 text-white px-8 sm:px-12 lg:px-16 py-10 lg:py-14 flex flex-col">
        {/* subtle grid overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(96,165,250,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,.35) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(ellipse at 30% 40%, black 40%, transparent 80%)",
          }}
        />
        {/* soft glow */}
        <div aria-hidden className="pointer-events-none absolute -right-40 top-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[120px]" />
        <div aria-hidden className="pointer-events-none absolute -left-32 bottom-10 w-[360px] h-[360px] rounded-full bg-sky-500/20 blur-[100px]" />

        {/* top logo */}
        <div className="relative flex items-center gap-3">
          <BrandLogo size={52} />
          <div>
            <div className="text-lg font-bold leading-none">{BRAND.companyName}</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-sky-300/80 mt-1.5 font-semibold">{BRAND.productName}</div>
          </div>
        </div>

        {/* headline block — grows to fill */}
        <div className="relative flex-1 flex flex-col justify-center max-w-xl mt-14 lg:mt-0">
          <span data-testid="hero-pill" className="inline-flex items-center gap-2 self-start px-3.5 py-1.5 rounded-full bg-white/5 ring-1 ring-white/10 backdrop-blur text-[11px] tracking-[0.2em] uppercase font-semibold text-sky-200">
            <Sparkles className="w-3.5 h-3.5 text-sky-300" /> {BRAND.pill}
          </span>
          <h1 className="mt-7 font-display text-5xl sm:text-6xl leading-[1.02] font-black tracking-tight">
            {BRAND.headline}
            <br />
            <span className="bg-gradient-to-r from-sky-300 via-blue-400 to-indigo-400 bg-clip-text text-transparent">{BRAND.tagline}</span>
          </h1>
          <p className="mt-6 text-slate-300/90 max-w-md text-base leading-relaxed">{BRAND.description}</p>

          {/* industry-like chips */}
          <div className="mt-8 flex flex-wrap gap-2">
            {CHIPS.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 ring-1 ring-white/10 text-xs font-semibold text-slate-200 hover:bg-white/10 transition">
                <Icon className="w-3.5 h-3.5 text-sky-300" /> {label}
              </span>
            ))}
          </div>

          {/* three small feature stat cards */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 backdrop-blur px-4 py-3">
                <div className="text-sm font-bold text-white">{f.title}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{f.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* footer bar */}
        <div className="relative mt-10 flex items-center justify-between text-xs text-slate-400">
          <div>{BRAND.copyright}</div>
          <div className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> 256-bit encrypted</div>
        </div>
      </section>

      {/* ==================== RIGHT: SIGN IN ==================== */}
      <section className="relative flex items-center justify-center px-6 sm:px-10 py-12 lg:py-16 bg-white">
        {/* soft radial background */}
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_75%_35%,rgba(59,130,246,0.08),transparent_55%)]" />

        {/* corner brand */}
        <div className="absolute top-6 right-6 hidden lg:flex items-center gap-2 text-slate-700">
          <BrandLogo size={30} />
          <span className="font-bold text-sm">{BRAND.companyName}</span>
        </div>

        <div className="relative w-full max-w-md">
          <div data-testid="login-eyebrow" className="text-[11px] tracking-[0.25em] uppercase font-semibold text-slate-500">Welcome back</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-2 tracking-tight">Sign in.</h2>
          <p className="mt-2 text-sm text-slate-500">
            Field workers, shop staff and admins — all in one portal.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                data-testid="login-email"
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition"
              />
            </div>

            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type={showPwd ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="login-password"
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-11 py-3.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                data-testid="login-toggle-pwd"
                aria-label={showPwd ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full rounded-xl bg-gradient-to-r from-slate-900 via-blue-900 to-blue-700 hover:from-slate-800 hover:to-blue-600 text-white font-semibold py-3.5 text-sm inline-flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20 disabled:opacity-60 transition"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  Sign in <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <div className="flex items-center gap-4 py-2">
              <div className="flex-1 h-px bg-slate-200" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-semibold">Secure sign in</div>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <p className="text-[11px] text-slate-500 text-center leading-relaxed">
              By signing in you agree to the demo <span className="font-semibold text-slate-700">Terms</span> &amp; <span className="font-semibold text-slate-700">Privacy Policy</span>.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
